import { LitElement, html, css } from "https://cdn.jsdelivr.net/npm/lit-element@4.1.1/+esm";
import { PASSWORD_RULES, validatePassword } from './passwordPolicyRules.js';
export {
	MIN_LENGTH,
	MAX_LENGTH,
	MAX_UTF8_BYTES,
	ALLOWED_SPECIAL_CHARACTERS,
	PASSWORD_RULES,
	utf8Length,
	containsControlCharacter,
	validatePassword,
} from './passwordPolicyRules.js';

// ─── Tenant theme (same detection as alertModal.js) ────────────────────────

function detectUIType() {
	const links = document.querySelectorAll('link[rel="stylesheet"]');
	for (const link of links) {
		const href = link.getAttribute('href') || '';
		if (href.includes('/css/admin-dark-theme.css')) return 'admin';
		if (href.includes('/legadom/')) return 'legadom';
	}
	return 'antilles';
}

function getTheme(uiType) {
	if (uiType === 'admin') {
		return { accent: '#03879e', accentLight: '#34d0e2', groupLabel: '#e0e0e0' };
	}
	if (uiType === 'legadom') {
		return { accent: '#111955', accentLight: '#1e2e7a', groupLabel: '#000000' };
	}
	return { accent: '#03879e', accentLight: '#34d0e2', groupLabel: '#000000' };
}

/**
 * Returns a strength level object based on the number of passed rules.
 * @param {number} passed
 * @param {number} total
 * @returns {{ level: 'none'|'weak'|'fair'|'strong'|'excellent', label: string, color: string, width: string }}
 */
function strengthLevel(passed, total) {
	if (passed === 0) return { level: 'none',      label: '',              color: '#e0e0e0', width: '0%'   };
	const ratio = passed / total;
	const width = `${Math.round(ratio * 100)}%`;
	if (ratio <= 0.35) return { level: 'weak',      label: 'Très faible',  color: '#e53935', width };
	if (ratio <= 0.55) return { level: 'fair',      label: 'Faible',       color: '#fb8c00', width };
	if (ratio <= 0.75) return { level: 'moderate',  label: 'Moyen',        color: '#fdd835', width };
	if (passed < total) return { level: 'strong',   label: 'Fort',         color: '#43a047', width };
	return               { level: 'excellent',     label: 'Excellent',    color: '#2e7d32', width: '100%' };
}

// ─── Web Component ───────────────────────────────────────────────────────────

/**
 * <password-strength> — Live password strength indicator.
 *
 * Attach to a password input via the `input-id` attribute:
 *
 *   <input type="password" id="password" ...>
 *   <password-strength input-id="password"></password-strength>
 *
 * The component observes the referenced input and updates automatically.
 * It exposes a `validate()` method that returns `{ ok, messages }`.
 */
export class PasswordStrength extends LitElement {

	static properties = {
		inputId:             { type: String, attribute: 'input-id' },
		requirementsSummary: { type: String, attribute: 'requirements-summary' },
		conciseChecklist:    { type: Boolean, attribute: 'concise-checklist' },
		_passed:             { type: Number, state: true },
		_results:            { type: Array,  state: true },
	};

	constructor() {
		super();
		this.inputId  = '';
		this.requirementsSummary = '';
		this.conciseChecklist = false;
		this._passed  = 0;
		this._results = PASSWORD_RULES.map(r => ({ ...r, ok: false }));
		this._theme   = getTheme(detectUIType());
		this._onInput = this._onInput.bind(this);
	}

	static get styles() {
		return css`
			:host {
				display: block;
				font-family: 'Segoe UI', Arial, sans-serif;
				font-size: 0.88rem;
				margin-top: 6px;
			}

			/* ── Bar shell ── */
			.bar-shell {
				height: 8px;
				background: #e0e0e0;
				border-radius: 4px;
				overflow: hidden;
				margin-bottom: 4px;
			}
			.bar-fill {
				height: 100%;
				border-radius: 4px;
				transition: width 0.35s ease, background-color 0.35s ease;
			}

			/* ── Label ── */
			.bar-label {
				font-size: 0.80rem;
				font-weight: 600;
				margin-bottom: 8px;
				min-height: 14px;
			}

			/* ── Rule list ── */
			.rules {
				list-style: none;
				margin: 0;
				padding: 0;
			}
			.rule {
				display: flex;
				align-items: center;
				gap: 6px;
				padding: 2px 0;
				color: #888;
				transition: color 0.2s ease;
			}
			.rule.ok {
				color: #2e7d32;
			}
			.rule-icon {
				flex-shrink: 0;
				font-size: 0.85rem;
				width: 16px;
				text-align: center;
			}
			.rule-group-label {
				list-style: none;
				font-size: 0.78rem;
				color: #000000;
				font-weight: 600;
				margin-top: 6px;
				padding: 0;
			}
		`;
	}

	connectedCallback() {
		super.connectedCallback();
		this._attachInput();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this._detachInput();
	}

	// Re-attach if the input-id attribute changes after mount
	updated(changed) {
		if (changed.has('inputId')) {
			this._detachInput();
			this._attachInput();
		}
	}

	_attachInput() {
		if (!this.inputId) return;
		const el = document.getElementById(this.inputId);
		if (el) el.addEventListener('input', this._onInput);
	}

	_detachInput() {
		if (!this.inputId) return;
		const el = document.getElementById(this.inputId);
		if (el) el.removeEventListener('input', this._onInput);
	}

	_onInput(e) {
		const value      = e.target.value || '';
		const { passed } = validatePassword(value);
		this._passed     = passed;
		this._results    = PASSWORD_RULES.map(rule => ({ ...rule, ok: rule.test(value) }));
	}

	/**
	 * Returns validation result for external use (form submit handler).
	 * @param {string} password - the password string to validate
	 * @returns {{ ok: boolean, messages: string[] }}
	 */
	validate(password) {
		const { ok, failures } = validatePassword(password);
		return { ok, messages: failures };
	}

	_displayRuleLabel(rule) {
		if (!this.conciseChecklist) return rule.label;
		return {
			length:    '12 caractères',
			uppercase: 'Au moins 1 lettre majuscule (A-Z)',
			lowercase: 'Au moins 1 lettre minuscule (a-z)',
			digit:     'Au moins 1 chiffre (0-9)',
			special:   'Au moins 1 caractère spécial',
		}[rule.id] || rule.label;
	}

	render() {
		const strength = strengthLevel(this._passed, PASSWORD_RULES.length);
		const t        = this._theme;

		return html`
			<style>
				.bar-fill  { background-color: ${strength.color}; }
				.rule.ok   { color: ${t.accent}; }
			</style>

			<div class="bar-shell">
				<div class="bar-fill" style="width: ${strength.width}"></div>
			</div>
			<div class="bar-label" style="color: ${strength.color}">
				${strength.label}
			</div>

			<ul class="rules">
				${this.requirementsSummary ? html`
					<li class="rule"><span>${this.requirementsSummary}</span></li>
				` : ''}
				${this.conciseChecklist ? html`
					<li class="rule-group-label"
						style="color: var(--password-group-label-color, ${t.groupLabel})">
						Les 4 catégories suivantes sont requises :
					</li>
				` : ''}
				${this._results
					.filter(r => r.mandatory && (!this.requirementsSummary || (this.conciseChecklist && r.id === 'length')))
					.map(r => html`
					<li class="rule ${r.ok ? 'ok' : ''}">
						<span class="rule-icon">${r.ok ? '✓' : '○'}</span>
						<span>${this._displayRuleLabel(r)}</span>
					</li>
				`)}
				${!this.conciseChecklist ? html`
					<li class="rule-group-label"
						style="color: var(--password-group-label-color, ${t.groupLabel})">
						Les 4 catégories suivantes sont requises :
					</li>
				` : ''}
				${this._results.filter(r => !r.mandatory).map(r => html`
					<li class="rule ${r.ok ? 'ok' : ''}">
						<span class="rule-icon">${r.ok ? '✓' : '○'}</span>
						<span>${this._displayRuleLabel(r)}</span>
					</li>
				`)}
			</ul>
		`;
	}
}

customElements.define('password-strength', PasswordStrength);
